import React from "react";
import EmptyState from "global/components/EmptyState/EmptyState";
import { EmptyStateSize } from "global/components/EmptyState/EmptyState.styles";
import { EmptyStateAccent } from "global/components/EmptyState/EmptyState.tokens";
import { EmptyStateIconName } from "global/components/EmptyState/EmptyStateIcons";

interface EmptyStateProps {
  title?: string;
  description?: string;
  bgColor?: string;
  buttons?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  }[];
  isMobile?: any;
  /** Chosen by the container. Chart panels and drawers are half-width cards. */
  size?: EmptyStateSize;
  icon?: EmptyStateIconName;
  accent?: EmptyStateAccent;
}

/**
 * Adapter kept for the call sites that already pass `title` / `description` /
 * `buttons`. It now draws the shared empty-state block
 * (`global/components/EmptyState`) rather than its own bare Paper, so those
 * screens picked up the design without each having to be rewritten.
 *
 * New code should use `EmptyState` directly — it takes a real icon, an accent
 * and a named primary/secondary pair instead of an untyped button array.
 */
const CustomEmptyState: React.FC<EmptyStateProps> = ({
  title = "No data available",
  description,
  buttons = [],
  bgColor,
  size = "medium",
  icon = "doc",
  accent = "brand",
}) => (
  <EmptyState
    size={size}
    icon={icon}
    accent={accent}
    title={title}
    body={description}
    primaryAction={
      buttons[0]
        ? { label: buttons[0].label, onClick: buttons[0].onClick }
        : undefined
    }
    secondaryAction={
      buttons[1]
        ? { label: buttons[1].label, onClick: buttons[1].onClick }
        : undefined
    }
    sx={bgColor ? { bgcolor: bgColor } : undefined}
  />
);

export default CustomEmptyState;
